import React, { useState, useEffect, useCallback } from 'react';
import Header from './components/Header';
import Footer from './components/Footer';
import { Routes, Route, useNavigate, useLocation, useParams, Navigate, Link } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { DirectoryProvider, useDirectoryContext } from './contexts/DirectoryContext';
import { getSSGPageData } from './lib/ssGData';
import { setOptions } from '@googlemaps/js-api-loader';
import LandingPage from './pages/LandingPage';
import DirectoryPage from './pages/DirectoryPage';
import GymProfilePage from './pages/GymProfilePage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import AuthPage from './pages/AuthPage';
import FavouritesPage from './pages/FavouritesPage';
import AccountPage from './pages/AccountPage';
import SavedSearchesPage from './pages/SavedSearchesPage';
import NswGymsHubPage from './pages/NswGymsHubPage';
import StateChooserPage from './pages/StateChooserPage';
import BlogIndexPage from './pages/BlogIndexPage';
import BlogPostPage from './pages/BlogPostPage';
import BlogCategoryPage from './pages/BlogCategoryPage';
import NotFoundPage from './pages/NotFoundPage';
import ScrollToTop from './components/ScrollToTop';
import { ToastProvider } from './src/components/ui/toast-provider';
import ProtectedRoute from './components/ProtectedRoute';
import { FilterState, Gym } from './types';
import { buildGymUrl } from './lib/slugs';
import { buildFilteredSearchUrl } from './lib/searchNavigation';
import { DEFAULT_STATE, stateDisplayToSlug } from './lib/stateCore.mjs';
import { STATE_ROUTE_PATHS, isStateDirectoryPath, CANONICAL_PROFILE_RE, buildStateDirectoryRootUrl, searchSurfaceForPathname } from './lib/stateRoutes';

// Initialize Google Maps JS API as early as possible (singleton handled by loader)
// Guard: setOptions internally accesses window.google which doesn't exist during SSG
const MAPS_KEY = (process.env as Record<string, string>).GOOGLE_MAPS_JS_KEY;
if (MAPS_KEY && typeof window !== 'undefined') {
  setOptions({ key: MAPS_KEY, v: 'weekly' });
}

import { extractGymIdentifier } from './utils/routeUtils';

// Regression Lock: deep-link extractor self-check (dev only)
if (process.env.NODE_ENV !== 'production') {
  const sampleWithG = "elite-mma-gChIJN1t_tDeuEmsRUsoyG83frY4";
  const resultWithG = extractGymIdentifier({ gymSlugWithId: sampleWithG }, {})?.id ?? null;

  const sampleWithoutG = "elite-mma-ChIJN1t_tDeuEmsRUsoyG83frY4";
  const resultWithoutG = extractGymIdentifier({ gymSlugWithId: sampleWithoutG }, {})?.id ?? null;

  if (resultWithG !== "ChIJN1t_tDeuEmsRUsoyG83frY4" || resultWithoutG !== "ChIJN1t_tDeuEmsRUsoyG83frY4") {
    // eslint-disable-next-line no-console
    console.warn("[Deep-Link Guard] extractGymIdentifier test failed.", { resultWithG, resultWithoutG });
  }
}

// Gym Profile Route component - separated for proper hook usage and re-rendering
interface GymProfileRouteProps {
  selectedGym: Gym | null;
  handleGymSelect: (gym: Gym) => void;
  onViewMap: () => void;
}

const GymProfileRoute: React.FC<GymProfileRouteProps> = ({
  selectedGym,
}) => {
  const { cachedSearchResults } = useDirectoryContext();
  // `navigate` was here for the removed onBack (2026-08-16); GymProfileRoute no longer routes.
  const location = useLocation() as any;
  // Canonical route: /nsw/:suburb/gyms/:gymSlugWithId; legacy /gyms/:suburb/:gymSlugWithId redirects via <NavigateToNewGymUrl>.
  const params = useParams<{ gymSlugWithId?: string; gymId?: string; suburb?: string }>();

  // Use the typed extractor that distinguishes stableId vs legacy placeId
  const gymIdentifier = extractGymIdentifier(params, location.state);

  // For backward compat: extract raw placeId for cache lookups
  const placeId = gymIdentifier?.id ?? null;

  // SSG: read pre-fetched gym injected by the build hook — but only adopt it when it
  // actually matches THIS route. The <script id="__SSG_DATA__"> tag persists in the
  // DOM after client navigation, so on a later gym it would otherwise keep returning
  // the FIRST SSG-loaded gym — pre-revealing (and, via the canonical-redirect effect
  // in GymProfilePage, navigating to) the wrong gym.
  const ssGRaw = getSSGPageData()?.gym ?? null;
  const ssGGym = (ssGRaw && placeId && (ssGRaw.stableId === placeId || ssGRaw.id === placeId)) ? ssGRaw : null;

  // Optimization: prefer SSG data, then navigation state, then search cache
  const initialGym = ssGGym ||
    (location.state?.gym as Gym) ||
    (placeId ? cachedSearchResults.find(g => g.id === placeId) : null) ||
    (selectedGym?.id === placeId ? selectedGym : null);

  if (!gymIdentifier) {
    // Definitive "not found" state for routing - redirect to directory or show info
    return (
      <div className="min-h-screen bg-[#F7F7F5] flex flex-col pt-header">
        <main id="main-content" className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#1F2328] mb-2">Gym link invalid</h1>
            <p className="text-text-muted mb-6">We couldn't find the gym you're looking for.</p>
            <Link
              to="/gyms"
              state={{ triggerFilters: true }}
              className="inline-block px-6 py-2 bg-[#2F5D9F] text-white rounded-lg font-medium hover:bg-[#254a7f] transition-colors"
            >
              Back to gyms
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    /* `onBack` is no longer passed: the gym page's Back button was removed by operator
       ruling on 2026-08-16, and it was this prop's only consumer. See the tombstones in
       GymProfilePage for what the two-mode control did and what a naive rebuild would
       lose. */
    <GymProfilePage
      key={placeId}
      placeId={placeId ?? ''}
      initialGym={initialGym}
    />
  );
};

// Legacy redirect: /gyms/:suburb/:gymSlugWithId → /nsw/:suburb/gyms/:gymSlugWithId
// Used to preserve backlinks from the old canonical gym-profile URL namespace.
const NavigateToNewGymUrl: React.FC = () => {
  const { suburb, gymSlugWithId } = useParams<{ suburb: string; gymSlugWithId: string }>();
  return <Navigate to={`/nsw/${suburb}/gyms/${gymSlugWithId}`} replace />;
};

// /[state]/:suburb/gyms/:gymSlugWithId disambiguation wrapper (registered per
// launched state from STATE_ROUTE_PATHS; formerly NswGymsSegmentRoute).
//
// Both gym-profile pages and style-filter pages share the same 4-segment path
// shape (/[state]/:suburb/gyms/:X). React Router v6 ranks them with equal
// specificity, so a second declared route would never match. This wrapper
// inspects the segment and branches at render time:
//
//   - /-g[A-Za-z0-9]+$/  → gym profile slug → render <GymProfileRoute>
//   - otherwise          → style slug → render <div/>, DirectoryPage overlay handles it
//
// The -g[id] suffix is the canonical disambiguator across routing and the
// static firebase.json redirects. FROZEN verbatim (R2 locked decision 3).
const GymsSegmentRoute: React.FC<GymProfileRouteProps> = (props) => {
  const { gymSlugWithId } = useParams<{ gymSlugWithId: string }>();
  if (gymSlugWithId && /-g[A-Za-z0-9]+$/.test(gymSlugWithId)) {
    return <GymProfileRoute {...props} />;
  }
  // Style-filter slug — DirectoryPage overlay (outside <Routes>) renders this.
  return <div />;
};

// Inner component that has access to router hooks
const AppRoutes: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);

  // Directory cache/filter atoms now live in DirectoryContext (item 50). AppRoutes only
  // RESETS them (handleLandingSearch), so it pulls just the setters; the values themselves
  // are read by the overlay + the gym-profile route wrappers via context.
  //
  // ⚠️ `setCachedSearchCenter` was pulled here too and is gone with `handleViewMap`
  // (operator-ruled 2026-08-16). TRACED BEFORE REMOVING, because dropping the last writer
  // of a shared state would freeze it silently rather than fail: `useDirectorySearch.ts:178`
  // still calls it on every resolved search, so the centre is written as before. This was
  // an unused DESTRUCTURE here, not the state's only writer.
  const {
    setActiveFilters,
    setCachedSearchResults,
    setCachedSearchLocation,
  } = useDirectoryContext();

  // Selected Gym for Profile View (out of scope for item 50; stays local to AppRoutes)
  const [selectedGym, setSelectedGym] = useState<Gym | null>(null);



  // No longer using getPath for navigation as we want canonical non-prefixed URLs
  // where possible, letting AuthContext/Middleware handle auth if needed.

  // Directly navigate to directory with filters from Landing Page
  const handleLandingSearch = useCallback((filters: FilterState, resolvedDisplay?: string) => {
    setActiveFilters(filters);
    setCachedSearchResults([]);
    setCachedSearchLocation('');

    if (filters.suburb) {
      // UI-01 hardening: DEFAULT_STATE here is an explicit product choice, not a
      // silent fallback — this is the state-LESS landing path (free-typed input or
      // a DEFAULT_STATE-resolved suggestion). LandingPage routes every non-default
      // resolved state — including the location button's server-returned stateSlug
      // since UI-28 shipped — through navigateToStateSearch instead, so it never
      // reaches here. The site-wide-state-memory slice (UI-09/SITE 8) remains the
      // deferred future for remembering a visitor's state across surfaces.
      const fullUrl = buildFilteredSearchUrl(filters, DEFAULT_STATE);
      // Carry the optimistic "Suburb Postcode" (when known) so the directory pill shows the
      // postcode from first paint instead of flicking it in after the search resolves.
      navigate(fullUrl, resolvedDisplay ? { state: { resolvedDisplay } } : undefined);
    } else {
      navigate('/gyms', { state: { triggerFilters: true } });
    }
  }, [navigate]);

  /**
   * 🪦 `handleViewMap` LIVED HERE and is gone with the Header's "Search Gyms" shortcut
   * (operator-ruled 2026-08-16). It cleared the cached search state — filters, results,
   * location, centre — and then navigated to the state-appropriate search surface,
   * carrying UI-57's review-caught rule that a visitor already inside a state goes to
   * THAT state's search rather than back to the chooser.
   *
   * ⚠️ THE HEADER WAS ITS ONLY CONSUMER, and I recorded the opposite before checking:
   * the commit that removed the prop carried a comment claiming this handler "still
   * serves the other consumers below". It did not. `tsc` named it dead in one run.
   * The other `onViewMap` props in this file belong to GymsSegmentRoute and
   * GymProfileRoute, which build their own inline navigate() — same name, different
   * prop, and reading the name as the relationship is what produced the false claim.
   *
   * ⚠️ WHAT WENT WITH IT, so a successor restoring a "search" shortcut knows what to
   * rebuild rather than rediscovering it: the CACHE CLEAR. Those inline handlers
   * navigate WITHOUT clearing cached results, so they land on a search surface that may
   * still be showing a previous query's state. That asymmetry predates this change and
   * is not introduced by it — but it is now the only behaviour of its kind left.
   */
  const handleGymSelect = useCallback((gym: Gym) => {
    setSelectedGym(gym);
    // App-wide gym click. Addressed from the gym's own state — this handler receives
    // gyms from the map, the search results and the landing page alike, so the page's
    // own path is not a reliable source for the gym's state.
    const gymUrl = buildGymUrl(
      gym.name, gym.stableId || gym.id, gym.suburb, gym.postcode,
      gym.state ? stateDisplayToSlug(gym.state) : DEFAULT_STATE,
    );
    navigate(gymUrl, { state: { gym } });
    window.scrollTo(0, 0);
  }, [navigate]);

  // Canonical: /[launched-state]/* = directory/profile namespace, /gyms =
  // directory root. Each state's suburbs index (/[state]/suburbs) is excluded so
  // the DirectoryPage overlay does not obscure it (UI-57: the exclusion followed
  // the index to its new address; /[state]/gyms is now directory space — the
  // state root). Derived from LAUNCHED_STATES via lib/stateRoutes.
  // UI-57: the '/gyms' disjunct is GONE — /gyms is the chooser page, not overlay
  // space; the per-state directory roots qualify via isStateDirectoryPath instead.
  const isDirectoryRoute = isStateDirectoryPath(location.pathname);

  // Profile route: canonical /[state]/:suburb/gyms/:slug-gID (derived from
  // LAUNCHED_STATES), legacy /gyms/:suburb/:slug-gID, or even-older legacy
  // /nsw/*/gym/*-gID (singular "gym") and /gym/* — the legacy patterns are
  // NSW-only permanently and stay literal (R2 locked decision 3).
  // The -g[A-Za-z0-9]+ suffix is the canonical disambiguator between profiles and styles.
  const isProfileRoute =
    CANONICAL_PROFILE_RE.test(location.pathname) ||                        // new canonical (per launched state)
    /^\/gyms\/[^/]+\/[^/]+-g[A-Za-z0-9]+$/.test(location.pathname) ||      // legacy: /gyms/:suburb/:slug
    /^\/nsw\/[^/]+\/gym\/[^/]+-g[A-Za-z0-9]+$/.test(location.pathname) ||  // legacy: singular /gym/
    location.pathname.startsWith('/gym/');

  // DirectoryPage is mounted above <Routes> as a persistent overlay (see the
  // <DirectoryPage> mount below) so its filter/search/scroll/map state survives
  // navigation between routes. This flag drives the overlay's CSS visibility
  // toggle (visibility:hidden + opacity-0 + z-[-1] + pointer-events-none) on
  // non-directory routes — but the overlay's DOM, INCLUDING its <h1>, remains
  // in every page's rendered HTML.
  //
  // Consequence: SEO audit tools report multiple <h1> elements on routes like
  // /nsw/gyms because the overlay's H1 coexists with the route's H1 in source.
  // Only one <h1> is ever visible to a user at a time, so this is an
  // audit-tool signal, not a user-facing or accessibility defect.
  //
  // Status: reviewed and ACCEPTED. Do NOT alter this toggle or move the
  // overlay mount to "fix" the multiple-H1 — doing so would sacrifice the
  // state-preservation behaviour for a non-issue.
  const isDirectoryVisible = isDirectoryRoute && !isProfileRoute && location.pathname !== '/';
  // UI-46 (operator-ruled 2026-08-08): the desktop hover-to-reveal auto-hide is
  // REMOVED. That also deleted the px-vs-rem THRESHOLD gate that used to live here
  // (`width >= 1024` in CSS pixels vs Tailwind's `lg:` = 64rem), which under a
  // non-16px root font disagreed and left the header overlapping content
  // permanently. Deleting the gate removed THIS instance, not the defect class.
  // The header's own height floor was the other half and is now the shared
  // `min-h-header` token (item 6) so bar
  // and offsets share a unit.
  //
  // ⚠️ CORRECTED 2026-08-14 (item 1). This comment previously said "one more
  // instance survives in `src/constants/blog.ts`". That was WRONG, and it was
  // wrong in the direction that stops anyone looking: a reader who trusted it
  // would have believed the class was down to a single cosmetic case. A content
  // census found NINE behavioural sites across FIVE files — Header (2, the
  // drawer/search resize handler, the only ones that could strand a visitor with
  // no navigation at all), FavouritesPage (2), DirectoryPage (1), blog.ts (1) and
  // LandingPage (3 px media queries the earlier count missed entirely). ALL NINE
  // are now migrated to `lib/breakpoints.ts`, which asks the browser the same REM
  // question the stylesheet asks. Image `sizes=` attributes are deliberately NOT
  // in scope: they are resource hints to the browser, not layout decisions that
  // must agree with Tailwind.

  // Scroll listener for global header
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Determine Header variant and props
  const isLandingPage = location.pathname === '/';
  const headerVariant = (isLandingPage && !isProfileRoute) ? 'transparent' : 'solid';

  return (
    <>
      {/* `onViewMap` is no longer passed: the Header's "Search Gyms" shortcut went with
          the per-state menus (operator-ruled 2026-08-16). ⚠️ The Header was `handleViewMap`'s
          ONLY consumer — an earlier draft of this comment claimed otherwise and `tsc` refuted
          it in one run — so the handler is gone too; see its tombstone above. */}
      <Header
        onGymSelect={handleGymSelect}
        variant={headerVariant as 'transparent' | 'solid'}
        isScrolled={location.pathname === '/' ? isScrolled : true}
      />

      {/* Persistent overlay — mounted above <Routes> so DirectoryPage's
          filter/search/scroll/map state survives navigation. See the
          isDirectoryVisible computation above for the accepted multiple-H1
          consequence of mounting it here. When off-route (isVisible=false) the
          shell applies aria-hidden + inert internally, so its duplicate <h1> and
          controls are dropped from the a11y tree / tab order / crawler view (G3). */}
      <DirectoryPage
        isVisible={isDirectoryVisible}
        onGymSelect={handleGymSelect}
      />

      <Routes>
        <Route
          path="/"
          element={
            <LandingPage
              onSearch={handleLandingSearch}
            />
          }
        />

        {/* Legacy redirect: old canonical /gyms/:suburb/:gymSlugWithId
            → new canonical /nsw/:suburb/gyms/:gymSlugWithId.
            Preserves backlinks and supports the SPA fallback when Firebase
            Hosting's 301 rule isn't in play (e.g. local dev). */}
        <Route path="/gyms/:suburb/:gymSlugWithId" element={<NavigateToNewGymUrl />} />

        {/* UI-57: /gyms is the STATE CHOOSER — a real routed page, no longer painted
            by the DirectoryPage overlay (isDirectoryRoute dropped its '/gyms'
            disjunct in the same tranche). */}
        <Route path="/gyms" element={<StateChooserPage />} />
        {/* Per-launched-state routes, derived from LAUNCHED_STATES via
            lib/stateRoutes (R2). While only NSW is launched these are
            byte-identical to the former hardcoded /nsw/... registrations;
            adding a state to LAUNCHED_STATES routes its tree with no edits
            here. React Router v6 ranks by specificity (the static /[state]/suburbs
            hub and the static /[state]/gyms directory root always outrank the
            :suburb param), so emission order across states is immaterial. The hub element (NswGymsHubPage — named for its
            origin, not its scope) is fully state-parameterised: R4 landed, and it
            renders every launched state's hub from the pathname. Do NOT build a
            per-state hub component; none is needed. */}
        {STATE_ROUTE_PATHS.map(({ state, hub }) => (
          <Route key={`${state}-hub`} path={hub} element={<NswGymsHubPage />} />
        ))}
        {/* UI-57: each state's DIRECTORY ROOT — the suburb-less search page that took
            the index's old address. Placeholder element like /gyms above: the
            persistent DirectoryPage overlay paints it (state-root mode keys off
            stateDirectoryRootSlug, not the route element). */}
        {STATE_ROUTE_PATHS.map(({ state }) => (
          <Route key={`${state}-directory-root`} path={buildStateDirectoryRootUrl(state)} element={<div />} />
        ))}
        {STATE_ROUTE_PATHS.map(({ state, directory }) => (
          <Route key={`${state}-directory`} path={directory} element={<div />} />
        ))}
        {/* ⚰️ TOMBSTONE — UI-73 registered '/[state]/gyms/:style' here (item 48's SEO half).
            REMOVED by UI-76, 2026-08-16, on an operator ruling: those urls were never intended
            to be pages, so item 48's SEO half is WON'T-DO and D&P's data join is cancelled.
            ⚠️ THE ABSENCE IS LOAD-BEARING AND MUST NOT BE "RESTORED". With no route registered
            these paths fall to the '*' catch-all below and render NotFoundPage over the shell —
            which is the intended state. Re-adding the <Route> WITHOUT D&P's manifest tier
            produces a third shape: Firebase serves notfound.html, React Router then matches and
            paints DirectoryPage over it — neither a real page nor a clean 404. Locked by
            lib/stateStyleWiring.test.ts. */}
        {/* Legacy UID-prefixed gym directory → redirect to clean /gyms */}
        <Route path="/:userId/gyms" element={<Navigate to="/gyms" replace />} />

        {/* CANONICAL: /[state]/:suburb/gyms/:gymSlugWithId (per launched state).
            Both gym-profile and style-filter URLs share this path shape. The
            <GymsSegmentRoute> wrapper branches on the -g[id] suffix:
              - matches -g[A-Za-z0-9]+ → gym profile page
              - otherwise              → style-filter directory (placeholder, overlay handles it) */}
        {STATE_ROUTE_PATHS.map(({ state, segment }) => (
          <Route
            key={`${state}-segment`}
            path={segment}
            element={
              <GymsSegmentRoute
                selectedGym={selectedGym}
                handleGymSelect={handleGymSelect}
                onViewMap={() => navigate(searchSurfaceForPathname(window.location.pathname), { state: { triggerFilters: true } })}
              />
            }
          />
        ))}

        {/* Even-older legacy: /nsw/:location/gym/:gymSlugWithId (singular "gym").
            Handled client-side by this fallback route; there is no server-side
            redirect for this pattern. */}
        <Route
          path="/nsw/:location/gym/:gymSlugWithId"
          element={
            <GymProfileRoute
              selectedGym={selectedGym}
              handleGymSelect={handleGymSelect}
              onViewMap={() => navigate(searchSurfaceForPathname(window.location.pathname), { state: { triggerFilters: true } })}
            />
          }
        />

        {/* Redirects for legacy /directory paths */}
        <Route path="/directory" element={<Navigate to="/gyms" replace />} />
        <Route path="/:userId/directory" element={<Navigate to="/gyms" replace />} />

        <Route
          path="/about"
          element={
            <AboutPage />
          }
        />
        <Route
          path="/contact"
          element={
            <ContactPage />
          }
        />
        <Route
          path="/privacy-policy"
          element={
            <PrivacyPage />
          }
        />
        <Route
          path="/terms-of-service"
          element={
            <TermsPage />
          }
        />
        <Route path="/auth" element={<AuthPage />} />

        {/* Blog — category route declared before /blog/:category/:slug.
            BlogCategoryPage validates the :category param at the component
            level (only the four known categories render content; anything
            else falls through to the not-found state). */}
        <Route path="/blog" element={<BlogIndexPage />} />
        <Route path="/blog/:category" element={<BlogCategoryPage />} />
        <Route path="/blog/:category/:slug" element={<BlogPostPage />} />

        {/* Protected authenticated routes — wrapped by ProtectedRoute auth guard */}
        <Route element={<ProtectedRoute />}>
          <Route path="/favourites" element={<FavouritesPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/account/saved-searches" element={<SavedSearchesPage />} />
        </Route>

        {/* Legacy routes → redirect to clean paths */}
        <Route path="/account/saved-search" element={<Navigate to="/account/saved-searches" replace />} />
        <Route path="/:userId/account/saved-search" element={<Navigate to="/account/saved-searches" replace />} />
        <Route path="/:userId/account" element={<Navigate to="/account" replace />} />
        <Route path="/:userId/favourites" element={<Navigate to="/favourites" replace />} />

        {/* Catch-all (audit L7). Lowest priority in React Router, so it only renders
            when nothing above matched. Before this existed, an unrecognised address
            rendered the complete homepage — the visitor never learned the link was
            wrong, and Google filed the URL as a homepage duplicate. Its server-side
            half is `** → /notfound.html` in firebase.json, which supplies the raw
            noindex without needing JS.

            NOTE it does NOT swallow the directory family: /[state]/:suburb/gyms and
            its style/gym children each have explicit routes above (several rendering
            <div/>, with the persistent DirectoryPage overlay painting them), so an
            unknown SUBURB still reaches the directory and its own empty state — which
            is the better message there. */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      {/* Global Footer — single mount inside AppRoutes. DirectoryPage's
          fixed inset-0 overlay (z-10 when visible) hides the Footer on
          /gyms-family routes by covering the full viewport. */}
      <Footer />
    </>
  );
};

const App: React.FC = () => {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.getElementById('root');
    if (root) root.style.visibility = 'visible';
  }, []);

  // No HelmetProvider: react-helmet-async was removed 2026-07-31. It was bundled and
  // mounted here but produced NOTHING under React 19 — proven with a MutationObserver
  // on <head> recording zero mutations across an in-app navigation. The tab title is
  // now set imperatively (hooks/useDocumentTitle.ts); every other head tag is owned by
  // the SSG, which is the copy crawlers read.
  return (
    <>
      <ScrollToTop />
      <AuthProvider>
        <ToastProvider>
          <DirectoryProvider>
            <AppRoutes />
          </DirectoryProvider>
        </ToastProvider>
      </AuthProvider>
    </>
  );
};

export default App;
